// Hook for email verification (after signup): verify-email + resend (enabled after 60s)
import { useState, useEffect } from 'react';
import { Form } from 'antd';
import { z } from 'zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMessage } from '@context/MessageProvider';
import { otpSchema } from '@validators/outside.schema';
import { useAuthStore } from '@stores/auth.store';
import { useVerifyEmailMutation, useResendVerificationMutation } from './useAuthMutations';

const RESEND_COOLDOWN_SECONDS = 60;

export const useVerifyEmail = () => {
    const [form] = Form.useForm();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { showMessage } = useMessage();
    const [countdown, setCountdown] = useState(RESEND_COOLDOWN_SECONDS);
    const verifyMutation = useVerifyEmailMutation();
    const resendMutation = useResendVerificationMutation();

    const email = searchParams.get('email') || '';

    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    const handleResend = async () => {
        if (!email) {
            showMessage('error', 'Email is required');
            return;
        }
        try {
            await resendMutation.mutateAsync({ email });
            setCountdown(RESEND_COOLDOWN_SECONDS);
        } catch (_) {
            // Error handled in mutation
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const onFinish = async (values) => {
        if (!email) {
            showMessage('error', 'Email is required');
            return;
        }
        try {
            const parsed = otpSchema.parse(values);
            const result = await verifyMutation.mutateAsync({ email, otp: parsed.otp });
            useAuthStore.getState().setSessionFromVerifyEmail(result);
            navigate('/onboarding', { replace: true });
        } catch (error) {
            if (error instanceof z.ZodError) {
                form.setFields([{ name: 'otp', errors: [error.errors[0]?.message || 'Invalid OTP'] }]);
            } else if (error?.message === 'OTP incorrect') {
                form.setFields([{ name: 'otp', errors: ['OTP incorrect'] }]);
            }
        }
    };

    return {
        form,
        loading: verifyMutation.isPending,
        countdown,
        resendLoading: resendMutation.isPending,
        email,
        onFinish,
        handleResend,
        formatTime,
    };
};
